/**
 * AdminRemittanceDepositsPage — CEO queue to confirm or reject gestor
 * remittance-fund deposits. Each row carries the declared amount, the
 * gestor, the method/network/tx_hash (for crypto) and quick action buttons.
 */
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2, ShieldCheck, ShieldAlert, Inbox, ArrowDownCircle, Filter,
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/contexts/AuthContext';
import { extractErrorMessage } from '@/utils/errors';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatusPill = ({ status }) => {
  const map = {
    pending:   { label: 'Pendiente',  klass: 'bg-amber-100 text-amber-700' },
    confirmed: { label: 'Confirmado', klass: 'bg-emerald-100 text-emerald-700' },
    rejected:  { label: 'Rechazado',  klass: 'bg-rose-100 text-rose-700' },
  };
  const m = map[status] || { label: status, klass: 'bg-slate-100 text-slate-600' };
  return <Badge className={`text-[10px] ${m.klass}`}>{m.label}</Badge>;
};

const AdminRemittanceDepositsPage = () => {
  const { getAuthHeaders } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/remittance-deposits${filter ? `?status=${filter}` : ''}`, { headers: getAuthHeaders() });
      setItems(r.data.items || []);
    } catch (err) {
      toast.error('Error al cargar depósitos');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, filter]);

  useEffect(() => { load(); }, [load]);

  const confirm = async (id) => {
    if (busyId) return;  // hard-guard against rapid double-taps
    setBusyId(id);
    try {
      const r = await axios.post(`${API}/admin/remittance-deposits/${id}/confirm`, {}, { headers: getAuthHeaders() });
      const kpis = r.data?.kpis || {};
      const uyu = kpis.UYU?.balance ?? 0;
      const usdt = kpis.USDT?.balance ?? 0;
      const prefix = r.data?.already_confirmed ? 'Ya estaba confirmado · ' : 'Depósito acreditado · ';
      toast.success(`${prefix}Saldo UYU: ${uyu.toFixed(2)} / USDT: ${usdt.toFixed(2)}`);
      await load();
    } catch (err) {
      // Backend 409 means the deposit transitioned past pending while we
      // were tapping. Refresh the list and tell the user, no panic toast.
      const status = err?.response?.status;
      if (status === 409) {
        toast.info('Este depósito ya fue procesado, refresco la lista.');
        await load();
      } else {
        toast.error(extractErrorMessage(err, 'No se pudo confirmar'));
      }
    } finally {
      setBusyId(null);
    }
  };

  const reject = async (id) => {
    const reason = window.prompt('Motivo del rechazo (opcional)');
    if (reason === null) return;
    setBusyId(id);
    try {
      await axios.post(`${API}/admin/remittance-deposits/${id}/reject${reason ? `?reason=${encodeURIComponent(reason)}` : ''}`, {}, { headers: getAuthHeaders() });
      toast.success('Depósito rechazado');
      load();
    } catch (err) {
      toast.error(extractErrorMessage(err, 'No se pudo rechazar'));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-recargas-page">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Inbox className="w-6 h-6 text-emerald-600" /> Recargas y depósitos
          </h1>
          <p className="text-xs text-slate-500 mt-1">Confirma los depósitos declarados por gestores para acreditar su fondo de remesas.</p>
        </div>
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-slate-500" />
          <Select value={filter || 'all'} onValueChange={(v) => setFilter(v === 'all' ? '' : v)}>
            <SelectTrigger className="w-44" data-testid="deposits-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="pending">Pendientes</SelectItem>
              <SelectItem value="confirmed">Confirmados</SelectItem>
              <SelectItem value="rejected">Rechazados</SelectItem>
              <SelectItem value="all">Todos</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <Card className="border-0 shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-slate-400"><Loader2 className="w-5 h-5 inline animate-spin" /> Cargando…</div>
        ) : items.length === 0 ? (
          <p className="p-8 text-center text-slate-400 text-sm">No hay depósitos en este filtro.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b text-left text-[10px] text-slate-500 uppercase">
                <th className="p-3">Gestor</th>
                <th className="p-3">Moneda</th>
                <th className="p-3 text-right">Monto</th>
                <th className="p-3">Método</th>
                <th className="p-3">Notas / Tx</th>
                <th className="p-3">Fecha</th>
                <th className="p-3">Estado</th>
                <th className="p-3 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {items.map((d) => (
                <tr key={d.id} className="border-b last:border-0 hover:bg-slate-50">
                  <td className="p-3">
                    <p className="font-semibold text-slate-800">{d.gestor?.name || d.gestor_id.slice(0, 8)}</p>
                    <p className="text-[10px] text-slate-400">{d.gestor?.email}</p>
                  </td>
                  <td className="p-3 font-semibold">
                    <span className="inline-flex items-center gap-1"><ArrowDownCircle className="w-3.5 h-3.5 text-emerald-600" /> {d.currency}</span>
                  </td>
                  <td className="p-3 text-right font-mono">{d.amount.toFixed(2)}</td>
                  <td className="p-3 text-xs text-slate-600">{d.method || '—'}{d.network ? ` (${d.network})` : ''}</td>
                  <td className="p-3 text-xs text-slate-500 max-w-xs truncate" title={d.tx_hash || d.notes || ''}>
                    {d.tx_hash || d.notes || '—'}
                  </td>
                  <td className="p-3 text-xs text-slate-400">{new Date(d.created_at).toLocaleString('es-ES')}</td>
                  <td className="p-3"><StatusPill status={d.status} /></td>
                  <td className="p-3 text-right">
                    {d.status === 'pending' ? (
                      <div className="flex justify-end gap-1">
                        <Button size="sm" className="h-7 px-2 bg-emerald-600 hover:bg-emerald-700" disabled={busyId === d.id} onClick={() => confirm(d.id)} data-testid={`deposit-confirm-${d.id}`}>
                          {busyId === d.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <ShieldCheck className="w-3 h-3 mr-1" />} Confirmar
                        </Button>
                        <Button size="sm" variant="outline" className="h-7 px-2" disabled={busyId === d.id} onClick={() => reject(d.id)} data-testid={`deposit-reject-${d.id}`}>
                          <ShieldAlert className="w-3 h-3 mr-1 text-rose-600" /> Rechazar
                        </Button>
                      </div>
                    ) : (
                      <span className="text-[10px] text-slate-400">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
};

export default AdminRemittanceDepositsPage;
